import { resolveRespectUserMotionPreference } from '../../common/motion';
import type { MotionPreferenceOptions } from '../../common/motion';

export const ATTR_MODAL_EFFECT = 'data-modal-effect';
export const ATTR_MODAL_EFFECT_FADE = 'data-modal-effect-fade';
export const ATTR_MODAL_FRAME_SURFACE = 'data-modal-frame-surface';
export const ATTR_MODAL_CHOOSE_MODE = 'data-modal-choose-mode';
export const ATTR_MODAL_CHOOSE_EFFECTIVE_MODE = 'data-modal-choose-effective-mode';
export const ATTR_MODAL_UI_MOTION = 'data-modal-ui-motion';

const MODAL_EFFECT_NAMES = ['none', 'scale-soft', 'scale-pop', 'blur'] as const;
const MODAL_CONTENT_EFFECT_NAMES = ['none', 'fade', 'blur', 'random'] as const;
const MODAL_CONTENT_EFFECT_INCLUDE_CHOOSE_LIST_VALUES = ['auto', 'always', 'never'] as const;
const MODAL_CONTENT_CHOOSER_MODES = ['single-viewer', 'chooser-viewer'] as const;
const MODAL_INDICATOR_PLACEMENTS = [
  'top-end',
  'top-center',
  'top-start',
  'bottom-end',
  'bottom-center',
  'bottom-start',
  'outside',
] as const;
const DEFAULT_MODAL_EFFECT_NAME: ModalEffectName = 'none';
const DEFAULT_MODAL_EFFECT_FADE = false;
const DEFAULT_MODAL_CONTENT_EFFECT_NAME: ModalContentEffectName = 'fade';
const DEFAULT_MODAL_CONTENT_EFFECT_INCLUDE_CHOOSE_LIST: ModalContentEffectIncludeChooseList = 'auto';
const DEFAULT_MODAL_CONTENT_CHOOSER_MODE: ModalContentChooserMode = 'chooser-viewer';
const DEFAULT_MODAL_INDICATOR_PLACEMENT: ModalIndicatorPlacement = 'bottom-center';
const DEFAULT_MODAL_FRAME_SURFACE_ENABLED = false;
const DEFAULT_MODAL_FRAME_SURFACE_BACKGROUND = '#fff';
const DEFAULT_MODAL_CONTENT_CHOOSER_LIST_ITEMS_PER_VIEW = 6;
const DEFAULT_MODAL_CONTENT_NAV_VISIBILITY_DELAY = 1500;
const DEFAULT_MODAL_CONTENT_NAV_VISIBILITY_BREAKPOINT = 767;
const CSS_MODAL_CONTENT_NAV_VISIBILITY_BREAKPOINT = '--modal-content-nav-visibility-breakpoint';
const SELECTOR_MODAL = '[data-modal]';
const DEFAULT_MODAL_UI_MOTION_MOVE_X = '0';
const DEFAULT_MODAL_UI_MOTION_MOVE_Y = '0';

/**
 * // Type: 汎用コンテンツ用ナビゲーションボタンの有効範囲
 * // Note:
 * // - data-modal-content-nav を持つボタン操作を制御します。
 * // - swipe / wheel など、ボタン以外の移動操作とは分けて扱います。
 */
export type ModalContentNavButtonOptions = {
  enabled?: boolean;
  item?: boolean;
  group?: boolean;
};

/**
 * // Type: スワイプ操作の有効範囲
 * // Note:
 * // - item は左右スワイプ、group は上下スワイプに対応します。
 */
export type ModalSwipeOptions = {
  enabled?: boolean;
  item?: boolean;
  group?: boolean;
};

/**
 * // Type: ホイール操作の有効範囲
 * // Note:
 * // - enabled は wheel 操作全体の有効 / 無効を制御します。
 * // - 現在の実装では wheel は group 移動専用です。
 * // - group を false にすると、wheel による prev-group / next-group 移動だけを無効化します。
 */
export type ModalWheelOptions = {
  enabled?: boolean;
  group?: boolean;
};

/**
 * // Type: video 再生制御の設定
 * // Note:
 * // - autoplay は描画後の自動再生を制御します。
 * // - resetOnChange は切替・閉じる時に currentTime を 0 に戻すかを制御します。
 */
export type ModalVideoOptions = {
  autoplay?: boolean;
  resetOnChange?: boolean;
};

/**
 * // Type: 汎用コンテンツ用ナビゲーションボタンの表示演出設定
 * // Note:
 * // - enabled を false にすると、遅延表示制御を使わず即時表示します。
 * // - hideDuringVideo は video 表示中に操作ボタンを隠すかどうかを制御します。
 */
export type ModalContentNavVisibilityOptions = {
  enabled?: boolean;
  delay?: number;
  hideDuringVideo?: boolean;
};

/**
 * // Type: モーダル内UIの表示切替演出設定
 * // Note:
 * // - content 差し替え中に、画像上のボタンやインジケーターを一時的にfadeさせます。
 * // - moveX / moveY を指定した場合だけ、fadeに軽い移動を足します。
 * // - duration / easing は TS option ではなく、SCSS側の CSS custom property で調整します。
 */
export type ModalUiMotionOptions = {
  enabled?: boolean;
  moveX?: string;
  moveY?: string;
};

/**
 * // Type: 汎用コンテンツの位置表示インジケーター配置
 */
export type ModalIndicatorPlacement = typeof MODAL_INDICATOR_PLACEMENTS[number];

/**
 * // Type: 汎用コンテンツの位置表示インジケーター設定
 * // Note:
 * // - enabled は item / group indicator 全体の有効 / 無効を制御します。
 * // - item は同一 group 内での「現在 item / item 総数」を制御します。
 * // - group は全 group 内での「現在 group / group 総数」を制御します。
 * // - create は表示用DOMをTS側で自動生成するかどうかを制御します。
 * // - create: false の場合は、手書きの data-modal-indicator / data-modal-indicator-group を更新対象にします。
 * // - placement は自動生成したインジケーターの配置を制御します。
 */
export type ModalIndicatorOptions = {
  enabled?: boolean;
  item?: boolean;
  group?: boolean;
  create?: boolean;
  placement?: ModalIndicatorPlacement;
};

/**
 * // Type: モーダル枠の surface 設定
 * // Note:
 * // - enabled が true の場合だけ、モーダル枠に背景面を付与します。
 * // - background は CSS custom property として反映し、案件側で上書きできます。
 */
export type ModalFrameSurfaceOptions = {
  enabled?: boolean;
  background?: string;
};

/**
 * // Type: モーダル枠の表示設定
 * // Note:
 * // - surface は背景面・パネル感を扱います。
 * // - 将来的に padding / radius / shadow などを追加しやすいよう frame 配下にまとめます。
 */
export type ModalFrameOptions = {
  surface?: ModalFrameSurfaceOptions;
};

/**
 * // Type: 汎用コンテンツの選択リスト表示モード
 * // Note:
 * // - single-viewer は表示領域だけを使い、モーダル内には choose list UI を表示しない構成です。
 * // - chooser-viewer は表示領域とモーダル内 choose list UI を併用する構成です。
 * // - mode は choose list UI の表示 / 非表示を決めます。
 */
export type ModalContentChooserMode = typeof MODAL_CONTENT_CHOOSER_MODES[number];

/**
 * // Type: 汎用コンテンツの選択リストUI設定
 * // Note:
 * // - create は持たせず、choose list UI を作るかどうかは choose.mode で決めます。
 * // - itemsPerView は、表示領域内に見せる choose item 数です。
 * // - bottom 配置では、この数を基準に1itemの幅を算出し、超過分は横スクロールで閲覧します。
 * // - 将来的に placement / className / template などを追加しやすいよう、list 配下にまとめます。
 */
export type ModalContentChooserListOptions = {
  itemsPerView?: number;
};

/**
 * // Type: 汎用コンテンツの選択リスト設定
 * // Note:
 * // - mode は choose list UI をモーダル内に表示するかどうかを切り替えます。
 * // - mode は prev-item / next-item の移動種別を直接決めません。
 * // - list は将来の choose list UI 拡張設定用に残します。
 * // - enabled は mode 未指定時の簡易指定として扱います。
 * // - hideWhenSingleItem は group 内 item が1件だけの場合に選択リストを非表示にします。
 * // - enabledGroups を指定した場合、その group だけ選択リストを有効にします。
 * // - disabledGroups は enabledGroups より優先し、指定 group の選択リストを無効にします。
 */
export type ModalContentChooserOptions = {
  mode?: ModalContentChooserMode;
  list?: ModalContentChooserListOptions;
  enabled?: boolean;
  hideWhenSingleItem?: boolean;
  enabledGroups?: string[];
  disabledGroups?: string[];
};

/**
 * // Type: 汎用コンテンツのループ移動設定
 * // Note:
 * // - item は同一 group 内の先頭 / 末尾で折り返すかを制御します。
 * // - group は先頭 / 末尾 group で折り返すかを制御します。
 * // - 初期値は false にし、端で止まる挙動を標準にします。
 */
export type ModalLoopOptions = {
  item?: boolean;
  group?: boolean;
};

/**
 * // Type: モーダルウィンドウの開閉エフェクト名
 * // Note:
 * // - fade は effect 名ではなく、effect.window.fade の boolean modifier として扱います。
 * // - scale-soft は控えめな拡大、scale-pop は0付近から立ち上がる強めの拡大です。
 * // - blur は fade と合成できる主 effect として扱います。
 * // - none は主 effect を追加しない状態です。
 */
export type ModalEffectName = typeof MODAL_EFFECT_NAMES[number];

/**
 * // Type: モーダルウィンドウ自体の開閉エフェクト名
 */
export type ModalWindowEffectName = ModalEffectName;

/**
 * // Type: content 差し替え時のエフェクト名
 * // Note:
 * // - fade は既存の content switch fade、blur は fade にぼかしを合成する effect です。
 * // - random は切替ごとに有効な content effect から1つを選びます。
 */
export type ModalContentEffectName = typeof MODAL_CONTENT_EFFECT_NAMES[number];

/**
 * // Type: group 移動時に choose list を content effect 対象へ含める条件
 * // Note:
 * // - auto は既存どおり、group が変わる場合だけ choose list を含めます。
 */
export type ModalContentEffectIncludeChooseList =
  typeof MODAL_CONTENT_EFFECT_INCLUDE_CHOOSE_LIST_VALUES[number];

/**
 * // Type: モーダルウィンドウ自体の開閉エフェクト設定
 * // Note:
 * // - enabled は window effect 全体の有効 / 無効を制御します。
 * // - name は scale / blur などの主 effect を指定します。
 * // - fade は単体fade、または主 effect への opacity 合成を制御します。
 */
export type ModalWindowEffectOptions = {
  enabled?: boolean;
  name?: ModalWindowEffectName;
  fade?: boolean;
};

/**
 * // Type: content 差し替え時のエフェクト設定
 * // Note:
 * // - enabled は content switch effect 全体の有効 / 無効を制御します。
 * // - includeChooseList は choose list も同時に content effect 対象へ含める条件を制御します。
 */
export type ModalContentEffectOptions = {
  enabled?: boolean;
  name?: ModalContentEffectName;
  includeChooseList?: ModalContentEffectIncludeChooseList;
};

/**
 * // Type: モーダルウィンドウのエフェクト設定
 * // Note:
 * // - window は modal 本体の開閉 effect、content は content 差し替え effect を担当します。
 * // - window effect は effect.window.name / effect.window.fade で指定します。
 */
export type ModalEffectOptions = {
  window?: ModalWindowEffectOptions;
  content?: ModalContentEffectOptions;
};

/**
 * // Type: モーダルウィンドウの responsive rule 設定
 * // Note:
 * // - breakpoint は max-width 条件として扱います。
 * // - options は現在 viewport が breakpoint 以下の場合に、base option へ上書き適用します。
 * // - 複数 rule が一致した場合は、配列の後ろに書いた rule を優先します。
 */
export type ModalResponsiveRuleOptions = {
  breakpoint?: number;
  options?: Partial<ModalDisplayOptions>;
};

/**
 * // Type: ModalDisplay の外部設定
 * // Note:
 * // - 省略時も補助UIは新しい標準仕様で解決します。
 * // - 必要な機能だけ false にして、用途別のモーダルを作れるようにします。
 * // - choose はモーダル内 choose list UI の正式設定です。
 * // - indicator は位置表示インジケーターの正式設定です。
 */
export type ModalDisplayOptions = MotionPreferenceOptions & {
  effect?: ModalEffectOptions;
  contentNavButton?: ModalContentNavButtonOptions;
  swipe?: ModalSwipeOptions;
  wheel?: ModalWheelOptions;
  video?: ModalVideoOptions;
  contentNavVisibility?: ModalContentNavVisibilityOptions;
  uiMotion?: ModalUiMotionOptions;
  indicator?: ModalIndicatorOptions;
  frame?: ModalFrameOptions;
  choose?: ModalContentChooserOptions;
  loop?: ModalLoopOptions;
  responsive?: ModalResponsiveRuleOptions[];
};

/**
 * // Type: 解決後の汎用コンテンツ選択リストUI設定
 */
export type ResolvedModalFrameSurfaceOptions = Required<ModalFrameSurfaceOptions>;

/**
 * // Type: 初期値を補完したモーダル枠設定
 */
export type ResolvedModalFrameOptions = {
  surface: ResolvedModalFrameSurfaceOptions;
};

export type ResolvedModalContentChooserListOptions = Required<ModalContentChooserListOptions>;

/**
 * // Type: 初期値を補完した汎用コンテンツ選択リスト設定
 */
export type ResolvedModalContentChooserOptions = {
  mode: ModalContentChooserMode;
  enabled: boolean;
  isModeExplicit: boolean;
  autoCreateList: boolean;
  list: ResolvedModalContentChooserListOptions;
  hideWhenSingleItem: boolean;
  enabledGroups: string[];
  disabledGroups: string[];
};

/**
 * // Type: 初期値を補完したモーダルウィンドウ自体の開閉エフェクト設定
 */
export type ResolvedModalWindowEffectOptions = Required<ModalWindowEffectOptions>;

/**
 * // Type: 初期値を補完した content 差し替えエフェクト設定
 */
export type ResolvedModalContentEffectOptions = Required<ModalContentEffectOptions>;

/**
 * // Type: 初期値を補完したモーダルウィンドウのエフェクト設定
 */
export type ResolvedModalEffectOptions = {
  window: ResolvedModalWindowEffectOptions;
  content: ResolvedModalContentEffectOptions;
};

/**
 * // Type: 初期値を補完した ModalDisplay 設定
 */
export type ResolvedModalDisplayOptions = {
  respectUserMotionPreference: boolean;
  effect: ResolvedModalEffectOptions;
  contentNavButton: Required<ModalContentNavButtonOptions>;
  swipe: Required<ModalSwipeOptions>;
  wheel: Required<ModalWheelOptions>;
  video: Required<ModalVideoOptions>;
  contentNavVisibility: Required<ModalContentNavVisibilityOptions>;
  uiMotion: Required<ModalUiMotionOptions>;
  indicator: Required<ModalIndicatorOptions>;
  frame: ResolvedModalFrameOptions;
  choose: ResolvedModalContentChooserOptions;
  loop: Required<ModalLoopOptions>;
};

/**
 * // Function: ModalDisplay の設定初期値を補完する
 * // Note:
 * // - 呼び出し側は必要な項目だけ指定できます。
 * // - contentNavVisibility.enabled 未指定時はPC即時表示 / SP遅延表示に寄せます。
 * // - hideDuringVideo 未指定時は video 表示中の前後ボタンを隠します。
 */
export function resolveModalDisplayOptions(
  options: ModalDisplayOptions = {},
  root_element: HTMLElement | null = null,
): ResolvedModalDisplayOptions
{
  const current_options: ModalDisplayOptions = applyModalResponsiveOptions(options);
  const video_autoplay: boolean = current_options.video?.autoplay ?? true;
  const is_content_nav_visibility_enabled: boolean = resolveDefaultContentNavVisibilityEnabled(
    current_options.contentNavVisibility?.enabled,
    root_element,
  );
  const choose_options: ModalContentChooserOptions | undefined = current_options.choose;
  const indicator_options: ModalIndicatorOptions | undefined = current_options.indicator;

  return {
    respectUserMotionPreference: resolveRespectUserMotionPreference(current_options.respectUserMotionPreference),
    effect: resolveModalEffectOptions(current_options.effect),
    contentNavButton: {
      enabled: current_options.contentNavButton?.enabled ?? true,
      item: current_options.contentNavButton?.item ?? true,
      group: current_options.contentNavButton?.group ?? true,
    },
    swipe: {
      enabled: current_options.swipe?.enabled ?? true,
      item: current_options.swipe?.item ?? true,
      group: current_options.swipe?.group ?? true,
    },
    wheel: {
      enabled: current_options.wheel?.enabled ?? true,
      group: current_options.wheel?.group ?? true,
    },
    video: {
      autoplay: video_autoplay,
      resetOnChange: current_options.video?.resetOnChange ?? true,
    },
    contentNavVisibility: {
      enabled: is_content_nav_visibility_enabled,
      delay: current_options.contentNavVisibility?.delay ?? DEFAULT_MODAL_CONTENT_NAV_VISIBILITY_DELAY,
      hideDuringVideo: current_options.contentNavVisibility?.hideDuringVideo ?? true,
    },
    uiMotion: resolveModalUiMotionOptions(current_options.uiMotion),
    indicator: resolveModalIndicatorOptions(indicator_options),
    frame: resolveModalFrameOptions(current_options.frame),
    choose: resolveModalContentChooserOptions(choose_options),
    loop: {
      item: current_options.loop?.item ?? false,
      group: current_options.loop?.group ?? false,
    },
  };
}


/**
 * // Function: 現在 viewport に一致する responsive rule を base option に上書き適用する
 * // Note:
 * // - breakpoint は max-width 条件です。
 * // - 複数 rule が一致した場合は、配列順に merge することで後勝ちにします。
 */
function applyModalResponsiveOptions(options: ModalDisplayOptions): ModalDisplayOptions
{
  const responsive_options: ModalDisplayOptions | undefined = getMatchedModalResponsiveOptions(
    options.responsive,
  );

  if (!responsive_options)
  {
    return options;
  }

  return mergeModalDisplayOptions(options, responsive_options);
}

/**
 * // Function: 現在 viewport に一致する responsive options を取得する
 */
function getMatchedModalResponsiveOptions(
  responsive_rules: ModalResponsiveRuleOptions[] | undefined,
): ModalDisplayOptions | undefined
{
  if (!responsive_rules || responsive_rules.length === 0)
  {
    return undefined;
  }

  return responsive_rules.reduce<ModalDisplayOptions | undefined>(
    (
      merged_options: ModalDisplayOptions | undefined,
      responsive_rule: ModalResponsiveRuleOptions,
    ) =>
    {
      if (!matchesModalResponsiveBreakpoint(responsive_rule.breakpoint))
      {
        return merged_options;
      }

      return mergeModalDisplayOptions(merged_options ?? {}, responsive_rule.options ?? {});
    },
    undefined,
  );
}

/**
 * // Function: responsive breakpoint の max-width 条件に一致しているか判定する
 */
function matchesModalResponsiveBreakpoint(breakpoint: number | undefined): boolean
{
  if (typeof breakpoint !== 'number' || !Number.isFinite(breakpoint) || breakpoint < 0)
  {
    return false;
  }

  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
  {
    return false;
  }

  return window.matchMedia(`(max-width: ${Math.floor(breakpoint)}px)`).matches;
}

/**
 * // Function: 汎用コンテンツ用ナビゲーションボタンの遅延表示初期値を解決する
 * // Note:
 * // - 明示指定がある場合は、その値を優先します。
 * // - 未指定時はPCでは即時表示、SP幅では閲覧性優先の遅延表示にします。
 */
function resolveDefaultContentNavVisibilityEnabled(
  option_enabled: boolean | undefined,
  root_element: HTMLElement | null,
): boolean
{
  if (option_enabled !== undefined)
  {
    return option_enabled;
  }

  return matchesModalResponsiveBreakpoint(
    resolveModalContentNavVisibilityBreakpoint(root_element),
  );
}

/**
 * // Function: 汎用コンテンツ用ナビゲーションボタンのSP判定境界値を取得する
 * // Note:
 * // - SCSS側の $BREAK_SP と同期するため、CSS custom property を優先して読みます。
 * // - 取得できない場合だけ固定の fallback を使います。
 */
export function resolveModalContentNavVisibilityBreakpoint(
  root_element: HTMLElement | null = null,
): number
{
  const custom_property_value: string = getModalContentNavVisibilityBreakpointValue(root_element);
  const parsed_breakpoint: number = parseModalPixelValue(custom_property_value);

  if (Number.isFinite(parsed_breakpoint) && parsed_breakpoint >= 0)
  {
    return parsed_breakpoint;
  }

  return DEFAULT_MODAL_CONTENT_NAV_VISIBILITY_BREAKPOINT;
}

/**
 * // Function: breakpoint 用CSS custom property の値を取得する
 */
function getModalContentNavVisibilityBreakpointValue(
  root_element: HTMLElement | null,
): string
{
  if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function')
  {
    return '';
  }

  const target_element: HTMLElement | null = findModalStyleTargetElement(root_element);

  if (!target_element)
  {
    return '';
  }

  return window
    .getComputedStyle(target_element)
    .getPropertyValue(CSS_MODAL_CONTENT_NAV_VISIBILITY_BREAKPOINT)
    .trim();
}

/**
 * // Function: modal 用CSS変数の参照対象を取得する
 */
function findModalStyleTargetElement(root_element: HTMLElement | null): HTMLElement | null
{
  if (root_element?.matches(SELECTOR_MODAL))
  {
    return root_element;
  }

  const scoped_modal: HTMLElement | null =
    root_element?.querySelector<HTMLElement>(SELECTOR_MODAL) ?? null;

  if (scoped_modal)
  {
    return scoped_modal;
  }

  if (typeof document === 'undefined')
  {
    return root_element;
  }

  return document.querySelector<HTMLElement>(SELECTOR_MODAL) ?? root_element;
}

/**
 * // Function: px値を数値へ変換する
 */
function parseModalPixelValue(value: string): number
{
  if (!value)
  {
    return Number.NaN;
  }

  const parsed_value: number = Number.parseFloat(value);

  if (!Number.isFinite(parsed_value))
  {
    return Number.NaN;
  }

  return parsed_value;
}

/**
 * // Function: ModalDisplayOptions を responsive rule 用に深めに merge する
 * // Note:
 * // - choose.list のような入れ子 option を rule 側の部分指定で上書きできるようにします。
 * // - responsive 自体は base 側を維持し、rule 内の responsive で再帰的に差し替えないようにします。
 */
function mergeModalDisplayOptions(
  base_options: ModalDisplayOptions,
  override_options: Partial<ModalDisplayOptions>,
): ModalDisplayOptions
{
  return {
    ...base_options,
    ...override_options,
    effect: mergeModalEffectOptions(base_options.effect, override_options.effect),
    contentNavButton: mergePlainOptions(base_options.contentNavButton, override_options.contentNavButton),
    swipe: mergePlainOptions(base_options.swipe, override_options.swipe),
    wheel: mergePlainOptions(base_options.wheel, override_options.wheel),
    video: mergePlainOptions(base_options.video, override_options.video),
    contentNavVisibility: mergePlainOptions(
      base_options.contentNavVisibility,
      override_options.contentNavVisibility,
    ),
    uiMotion: mergePlainOptions(base_options.uiMotion, override_options.uiMotion),
    indicator: mergePlainOptions(base_options.indicator, override_options.indicator),
    frame: mergeModalFrameOptions(base_options.frame, override_options.frame),
    choose: mergeModalContentChooserOptions(base_options.choose, override_options.choose),
    loop: mergePlainOptions(base_options.loop, override_options.loop),
    responsive: base_options.responsive,
  };
}

/**
 * // Function: effect option を responsive rule 用に merge する
 */
function mergeModalEffectOptions(
  base_options: ModalEffectOptions | undefined,
  override_options: ModalEffectOptions | undefined,
): ModalEffectOptions | undefined
{
  if (!base_options && !override_options)
  {
    return undefined;
  }

  return {
    ...base_options,
    ...override_options,
    window: mergePlainOptions(base_options?.window, override_options?.window),
    content: mergePlainOptions(base_options?.content, override_options?.content),
  };
}

/**
 * // Function: frame option を responsive rule 用に merge する
 */
function mergeModalFrameOptions(
  base_options: ModalFrameOptions | undefined,
  override_options: ModalFrameOptions | undefined,
): ModalFrameOptions | undefined
{
  if (!base_options && !override_options)
  {
    return undefined;
  }

  return {
    ...base_options,
    ...override_options,
    surface: mergePlainOptions(base_options?.surface, override_options?.surface),
  };
}

/**
 * // Function: choose option を responsive rule 用に merge する
 */
function mergeModalContentChooserOptions(
  base_options: ModalContentChooserOptions | undefined,
  override_options: ModalContentChooserOptions | undefined,
): ModalContentChooserOptions | undefined
{
  if (!base_options && !override_options)
  {
    return undefined;
  }

  return {
    ...base_options,
    ...override_options,
    list: mergePlainOptions(base_options?.list, override_options?.list),
  };
}

/**
 * // Function: 1階層の option を responsive rule 用に merge する
 */
function mergePlainOptions<T extends object>(
  base_options: T | undefined,
  override_options: T | undefined,
): T | undefined
{
  if (!base_options && !override_options)
  {
    return undefined;
  }

  return {
    ...base_options,
    ...override_options,
  } as T;
}


/**
 * // Function: モーダル枠の設定を解決する
 */
function resolveModalFrameOptions(
  options: ModalFrameOptions | undefined,
): ResolvedModalFrameOptions
{
  return {
    surface: {
      enabled: options?.surface?.enabled ?? DEFAULT_MODAL_FRAME_SURFACE_ENABLED,
      background: options?.surface?.background ?? DEFAULT_MODAL_FRAME_SURFACE_BACKGROUND,
    },
  };
}


/**
 * // Function: モーダル内UIの表示切替演出設定を解決する
 * // Note:
 * // - enabled 未指定時は true にし、content 切り替え時のUI fadeを標準化します。
 * // - moveX / moveY はCSS変数として反映し、どちらも0の場合は見た目上fadeのみになります。
 */
function resolveModalUiMotionOptions(
  options: ModalUiMotionOptions | undefined,
): Required<ModalUiMotionOptions>
{
  return {
    enabled: options?.enabled ?? true,
    moveX: options?.moveX ?? DEFAULT_MODAL_UI_MOTION_MOVE_X,
    moveY: options?.moveY ?? DEFAULT_MODAL_UI_MOTION_MOVE_Y,
  };
}


/**
 * // Function: 汎用コンテンツの位置表示インジケーター設定を解決する
 * // Note:
 * // - create 未指定時は true にし、item indicator を標準で自動生成します。
 * // - group 未指定時は false にし、必要な場合だけ group indicator を有効化します。
 */
function resolveModalIndicatorOptions(
  options: ModalIndicatorOptions | undefined,
): Required<ModalIndicatorOptions>
{
  return {
    enabled: options?.enabled ?? true,
    item: options?.item ?? true,
    group: options?.group ?? false,
    create: options?.create ?? true,
    placement: resolveModalIndicatorPlacement(options?.placement),
  };
}

/**
 * // Function: 位置表示インジケーターの配置指定を解決する
 */
function resolveModalIndicatorPlacement(
  placement: ModalIndicatorPlacement | undefined,
): ModalIndicatorPlacement
{
  if (placement && MODAL_INDICATOR_PLACEMENTS.includes(placement))
  {
    return placement;
  }

  return DEFAULT_MODAL_INDICATOR_PLACEMENT;
}

/**
 * // Function: 汎用コンテンツの選択リスト設定を解決する
 * // Note:
 * // - mode 指定時は mode を優先します。
 * // - mode 未指定時は enabled 指定を読み取り、簡易指定を許容します。
 * // - mode / enabled 未指定時は chooser-viewer とし、choose list UI を標準表示します。
 * // - enabled: true は chooser-viewer、enabled: false は single-viewer に変換します。
 * // - choose list UI の自動生成は、mode: 'chooser-viewer' の場合に有効にします。
 */
function resolveModalContentChooserOptions(
  options: ModalContentChooserOptions | undefined,
): ResolvedModalContentChooserOptions
{
  const is_mode_explicit: boolean = options?.mode !== undefined;
  const mode: ModalContentChooserMode = resolveModalContentChooserMode(
    options?.mode,
    options?.enabled,
  );

  return {
    mode,
    enabled: mode === 'chooser-viewer',
    isModeExplicit: is_mode_explicit,
    autoCreateList: mode === 'chooser-viewer',
    list: resolveModalContentChooserListOptions(options?.list),
    hideWhenSingleItem: options?.hideWhenSingleItem ?? false,
    enabledGroups: options?.enabledGroups ?? [],
    disabledGroups: options?.disabledGroups ?? [],
  };
}

/**
 * // Function: 汎用コンテンツの選択リストUI設定を解決する
 * // Note:
 * // - 現時点では create を扱わず、choose list UI の生成有無は choose.mode に集約します。
 * // - itemsPerView は表示領域内に何個分の choose item を見せるかを決めます。
 * // - list は将来の placement / className / template 追加用の受け皿として残します。
 */
function resolveModalContentChooserListOptions(
  options: ModalContentChooserListOptions | undefined,
): ResolvedModalContentChooserListOptions
{
  return {
    itemsPerView: resolvePositiveIntegerOption(
      options?.itemsPerView,
      DEFAULT_MODAL_CONTENT_CHOOSER_LIST_ITEMS_PER_VIEW,
    ),
  };
}

/**
 * // Function: 1以上の整数 option を解決する
 */
function resolvePositiveIntegerOption(
  option_value: number | undefined,
  fallback: number,
): number
{
  if (typeof option_value !== 'number' || !Number.isFinite(option_value))
  {
    return fallback;
  }

  const integer_value: number = Math.floor(option_value);

  return integer_value >= 1 ? integer_value : fallback;
}

/**
 * // Function: 汎用コンテンツの選択リスト表示モードを解決する
 */
function resolveModalContentChooserMode(
  option_mode: ModalContentChooserMode | undefined,
  option_enabled: boolean | undefined,
): ModalContentChooserMode
{
  if (isModalContentChooserMode(option_mode))
  {
    return option_mode;
  }

  if (option_enabled !== undefined)
  {
    return option_enabled ? 'chooser-viewer' : 'single-viewer';
  }

  return DEFAULT_MODAL_CONTENT_CHOOSER_MODE;
}

/**
 * // Function: 汎用コンテンツの選択リスト表示モードとして扱える値か判定する
 */
function isModalContentChooserMode(value: string | undefined): value is ModalContentChooserMode
{
  if (!value)
  {
    return false;
  }

  return MODAL_CONTENT_CHOOSER_MODES.includes(value as ModalContentChooserMode);
}

/**
 * // Function: モーダルウィンドウの effect 設定を解決する
 * // Note:
 * // - window はモーダル本体の開閉 effect を制御します。
 * // - content は content 差し替え effect を制御します。
 * // - content switch effect は window effect とは独立して、未指定時も有効にします。
 */
function resolveModalEffectOptions(
  options: ModalEffectOptions | undefined,
): ResolvedModalEffectOptions
{
  return {
    window: resolveModalWindowEffectOptions(options?.window),
    content: resolveModalContentEffectOptions(options?.content),
  };
}

/**
 * // Function: モーダルウィンドウ自体の開閉 effect 設定を解決する
 */
function resolveModalWindowEffectOptions(
  options: ModalWindowEffectOptions | undefined,
): ResolvedModalWindowEffectOptions
{
  const effect_name: ModalWindowEffectName = resolveModalEffectName(options?.name);
  const should_fade: boolean = options?.fade ?? DEFAULT_MODAL_EFFECT_FADE;

  return {
    enabled: options?.enabled ?? (effect_name !== 'none' || should_fade),
    name: effect_name,
    fade: should_fade,
  };
}

/**
 * // Function: content 差し替え effect 設定を解決する
 */
function resolveModalContentEffectOptions(
  options: ModalContentEffectOptions | undefined,
): ResolvedModalContentEffectOptions
{
  const effect_name: ModalContentEffectName = resolveModalContentEffectName(options?.name);

  return {
    enabled: options?.enabled ?? effect_name !== 'none',
    name: effect_name,
    includeChooseList: resolveModalContentEffectIncludeChooseList(options?.includeChooseList),
  };
}

/**
 * // Function: モーダルウィンドウの effect 名を解決する
 */
function resolveModalEffectName(
  option_effect_name: ModalEffectName | undefined,
): ModalEffectName
{
  if (isModalEffectName(option_effect_name))
  {
    return option_effect_name;
  }

  return DEFAULT_MODAL_EFFECT_NAME;
}

/**
 * // Function: モーダルウィンドウの effect 名として扱える値か判定する
 */
function isModalEffectName(value: string | undefined): value is ModalEffectName
{
  if (!value)
  {
    return false;
  }

  return MODAL_EFFECT_NAMES.includes(value as ModalEffectName);
}

/**
 * // Function: content 差し替え effect 名を解決する
 */
function resolveModalContentEffectName(
  option_effect_name: ModalContentEffectName | undefined,
): ModalContentEffectName
{
  if (isModalContentEffectName(option_effect_name))
  {
    return option_effect_name;
  }

  return DEFAULT_MODAL_CONTENT_EFFECT_NAME;
}

/**
 * // Function: content 差し替え effect 名として扱える値か判定する
 */
function isModalContentEffectName(value: string | undefined): value is ModalContentEffectName
{
  if (!value)
  {
    return false;
  }

  return MODAL_CONTENT_EFFECT_NAMES.includes(value as ModalContentEffectName);
}

/**
 * // Function: choose list を content effect 対象へ含める条件を解決する
 */
function resolveModalContentEffectIncludeChooseList(
  option_value: ModalContentEffectIncludeChooseList | undefined,
): ModalContentEffectIncludeChooseList
{
  if (isModalContentEffectIncludeChooseList(option_value))
  {
    return option_value;
  }

  return DEFAULT_MODAL_CONTENT_EFFECT_INCLUDE_CHOOSE_LIST;
}

/**
 * // Function: choose list を content effect 対象へ含める条件として扱える値か判定する
 */
function isModalContentEffectIncludeChooseList(
  value: string | undefined,
): value is ModalContentEffectIncludeChooseList
{
  if (!value)
  {
    return false;
  }

  return MODAL_CONTENT_EFFECT_INCLUDE_CHOOSE_LIST_VALUES.includes(
    value as ModalContentEffectIncludeChooseList,
  );
}
